import { UUID } from "io-ts-types/UUID";

import {
  NpsUserKind,
  saveNpsAnswersRouteSpec,
  saveNpsScoreRouteSpec,
  saveNpsShownRouteSpec,
} from "@every.org/common/src/routes/nps";

import { NpsActionType, dispatchNpsAction } from "src/context/NpsContext";
import { trackEvent } from "src/utility/analytics";
import { queryApi } from "src/utility/apiClient";
import { logger } from "src/utility/logger";

export function hideNps(): void {
  dispatchNpsAction({ type: NpsActionType.HIDE_NPS });
}

export function nextNpsStep(): void {
  dispatchNpsAction({ type: NpsActionType.NEXT_STEP });
}

export function setNpsScore(score: number): void {
  dispatchNpsAction({ type: NpsActionType.SET_SCORE, score });
}

export async function submitNpsShown({
  userKind,
  nonprofitId,
}: {
  userKind: NpsUserKind;
  nonprofitId: UUID | null;
}): Promise<void> {
  try {
    await queryApi(saveNpsShownRouteSpec, {
      routeTokens: {},
      queryParams: {},
      body: { userKind, ...(nonprofitId ? { nonprofitId } : {}) },
    });
    dispatchNpsAction({
      type: NpsActionType.SET_LAST_NPS_SHOWN_AT,
      value: new Date().toISOString(),
    });
  } catch (e) {
    logger.warn({ message: "NPS: failed to save shown event", error: e });
  }

  trackEvent("nps_shown", {
    user_type: userKind,
  });
}

export async function submitNpsScore({
  score,
  userKind,
  nonprofitId,
}: {
  score: number;
  userKind: NpsUserKind;
  nonprofitId: UUID | null;
}): Promise<void> {
  try {
    await queryApi(saveNpsScoreRouteSpec, {
      routeTokens: {},
      queryParams: {},
      body: { userKind, score, ...(nonprofitId ? { nonprofitId } : {}) },
    });
  } catch (e) {
    logger.warn({ message: "NPS: failed to save score", error: e });
  }

  trackEvent("nps_score_submitted", {
    user_type: userKind,
    score: String(score),
    org_id: nonprofitId ?? "",
  });
}

export async function submitNpsCheckboxes({
  question,
  selections,
  userKind,
  nonprofitId,
}: {
  question: string;
  selections: string[];
  userKind: NpsUserKind;
  nonprofitId: UUID | null;
}): Promise<void> {
  try {
    await queryApi(saveNpsAnswersRouteSpec, {
      routeTokens: {},
      queryParams: {},
      body: {
        userKind,
        question,
        answer: selections,
        ...(nonprofitId ? { nonprofitId } : {}),
      },
    });
  } catch (e) {
    logger.warn({ message: "NPS: failed to save checkboxes", error: e });
  }

  trackEvent("nps_checkboxes_submitted", {
    user_type: userKind,
    selections: selections.join(","),
  });
}

export async function submitNpsFeedbackText({
  text,
  userKind,
  nonprofitId,
}: {
  text: string | null;
  userKind: NpsUserKind;
  nonprofitId: UUID | null;
}): Promise<void> {
  if (text) {
    try {
      await queryApi(saveNpsAnswersRouteSpec, {
        routeTokens: {},
        queryParams: {},
        body: {
          userKind,
          question: "feedback",
          answer: text,
          ...(nonprofitId ? { nonprofitId } : {}),
        },
      });
    } catch (e) {
      logger.warn({ message: "NPS: failed to save feedback text", error: e });
    }
  }

  trackEvent("nps_text_submitted", {
    user_type: userKind,
    has_text: text ? "true" : "false",
  });
}
